import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../services/api';

export default function SuccessStoriesPage() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const response = await contentAPI.successStories();
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-12" data-testid="success-stories-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-heading text-2xl text-white">Success Stories</h1>
            <p className="text-sm text-gray-400 font-body">Real love stories from our community</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="px-6 py-8 text-center">
        <Heart className="w-12 h-12 text-gold mx-auto mb-4" fill="#D4AF37" />
        <h2 className="font-heading text-2xl text-white mb-2">
          Love Knows No Borders
        </h2>
        <p className="text-gray-400 font-body max-w-md mx-auto">
          Thousands of couples have found their soulmate on African Connections
        </p>
      </div>

      {/* Stories */}
      <div className="px-6 space-y-6">
        {stories.map((story, index) => (
          <motion.div
            key={story.story_id || index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-dark-paper border border-white/5 rounded-2xl overflow-hidden"
          >
            {/* Photo */}
            <div className="aspect-video relative">
              <img
                src={story.photo_url || 'https://images.pexels.com/photos/15367697/pexels-photo-15367697.jpeg'}
                alt={story.couple_names}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="font-heading text-xl text-white">{story.couple_names}</h3>
                <p className="text-gold text-sm font-body">{story.countries}</p>
                {story.married_year && (
                  <p className="text-gray-400 text-xs font-body mt-1">Married {story.married_year}</p>
                )}
              </div>
            </div>

            {/* Testimonial */}
            <div className="p-6">
              <Quote className="w-8 h-8 text-gold/30 mb-3" />
              <p className="text-gray-300 font-body italic leading-relaxed">
                {story.testimonial}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-6 py-8 text-center">
        <p className="text-gray-400 font-body mb-4">Ready to write your own love story?</p>
        <button
          onClick={() => navigate('/register')}
          className="btn-gold px-8 py-3 rounded-full font-body font-semibold"
          data-testid="get-started-btn"
        >
          Get Started Free
        </button>
      </div>
    </div>
  );
}
